import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { css } from "@/domains/emotion";
import { useAppStore } from "@/store";
import { GatedFeature } from "@/store/feature-gates/types";
import { MDSProgressBar, MDSProgressBarColor, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { useEffect, useState } from "react";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

type GateCheckResult = {
  allowed: boolean;
  used: number;
  limit: number;
  warnAt: number | null;
};

export const UsageProgress = observer(() => {
  const { store } = useAppStore();

  const [notes, setNotes] = useState<GateCheckResult | null>(null);
  const [chat, setChat] = useState<GateCheckResult | null>(null);
  const [pdf, setPdf] = useState<GateCheckResult | null>(null);

  useEffect(() => {
    setChat(store.featureGates.checkGate(GatedFeature.ChatMessageSend));
    // Observe sessions and session events to subscribe and activate the live query
  }, [store.sessionEvents.size, store.sessions.size, store.featureGates]);

  useEffect(() => {
    setPdf(store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage));
  }, [store.featureGates, store.featureGates.pdfPagesProcessedCount]);

  useEffectOnMount(() => {
    Promise.all([
      store.featureGates.checkGate(GatedFeature.NoteCreate),
      store.featureGates.checkGate(GatedFeature.ChatMessageSend),
    ])
      .then(([notesResult, chatResult]) => {
        setNotes(notesResult);
        setChat(chatResult);
        setPdf(store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage));
      })
      .catch(error => {
        logger.error({
          message: "[SYNC][UsageProgress] Error checking feature gates",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
  });

  const formatValue = (usage: GateCheckResult | null) => {
    if (!usage) return "—";
    return `${usage.used} / ${usage.limit === Infinity ? "∞" : usage.limit}`;
  };

  const progressColor = (usage: GateCheckResult | null): MDSProgressBarColor => {
    if (!usage) return MDSProgressBarColor.Default;
    if (usage.limit === Infinity || usage.allowed) return MDSProgressBarColor.Emphasis;
    return MDSProgressBarColor.Danger;
  };

  const progressMax = (usage: GateCheckResult | null): number => {
    if (!usage) return 25;
    return usage.limit === Infinity ? Math.max(1, usage.used) : usage.limit;
  };

  return (
    <RowGrid>
      {/* Notes row */}
      <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
        New notes
      </MDSText>
      <MDSProgressBar
        className={inlineBarStyles}
        value={notes?.used ?? 0}
        minValue={0}
        maxValue={progressMax(notes)}
        aria-valuetext={formatValue(notes)}
        size="xlarge"
        color={progressColor(notes)}
        animation={{ duration: 0 }}
        rounded
      />
      <MDSText variant={MDSTextVariant.TextS} className={rightValueStyles}>
        {formatValue(notes)}
      </MDSText>

      {/* Chat row */}
      <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
        Chat messages
      </MDSText>
      <MDSProgressBar
        className={inlineBarStyles}
        value={chat?.used ?? 0}
        minValue={0}
        maxValue={progressMax(chat)}
        aria-valuetext={formatValue(chat)}
        size="xlarge"
        color={progressColor(chat)}
        animation={{ duration: 0 }}
        rounded
      />
      <MDSText variant={MDSTextVariant.TextS} className={rightValueStyles}>
        {formatValue(chat)}
      </MDSText>

      {/* PDF row */}
      <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
        PDF limits
      </MDSText>
      <MDSProgressBar
        className={inlineBarStyles}
        value={pdf?.used ?? 0}
        minValue={0}
        maxValue={progressMax(pdf)}
        aria-valuetext={formatValue(pdf)}
        size="xlarge"
        color={progressColor(pdf)}
        animation={{ duration: 0 }}
        rounded
      />
      <MDSText variant={MDSTextVariant.TextS} className={rightValueStyles}>
        {formatValue(pdf)}
      </MDSText>
    </RowGrid>
  );
});

const RowGrid = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "max-content 1fr max-content",
  alignItems: "center",
  columnGap: 16,
  rowGap: theme.spacing[5],
  margin: `${theme.spacing[5]} 0`,
  width: "100%",
}));

// Override the internal grid of MDSProgressBar to render only the bar inline
const inlineBarStyles = css({
  display: "grid",
  gridTemplateAreas: '"bar"',
  gridTemplateColumns: "1fr",
  gap: 0,
  width: "100%",
  // Disable any animations/transitions inside the MDSProgressBar
  ".progress-fill": {
    transition: "none !important",
    animation: "none !important",
  },
});

const rightValueStyles = css({ display: "flex", alignItems: "center", justifyContent: "flex-end" });
